import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { useBranding } from "@/hooks/useBranding";
import { Airplane, Eye, EyeSlash, Compass } from "@phosphor-icons/react";

const Login = () => {
  const { login } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      // Route to a role-appropriate landing page:
      //   • referrer  → /my-referrals
      //   • sales_agent → /quick-quote (dashboard often hidden by settings)
      //   • accounts  → /reports
      //   • everyone else (admin/manager) → intended `from` or /
      let dest = from;
      if (u?.role === "referrer") dest = "/my-referrals";
      else if (from === "/" && u?.role === "sales_agent") dest = "/quick-quote";
      else if (from === "/" && u?.role === "accounts") dest = "/reports";
      navigate(dest, { replace: true });
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-stretch bg-background" data-testid="login-page">
      {/* Brand panel (hidden on mobile) */}
      <div
        className="hidden lg:flex relative w-1/2 brand-gradient text-white overflow-hidden"
        style={branding.loginBg ? { backgroundImage: `linear-gradient(135deg, hsl(var(--primary) / 0.85), hsl(var(--primary) / 0.65)), url(${branding.loginBg})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        data-testid="login-brand-panel"
      >
        <div className="aurora" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            {branding.logo ? (
              <img src={branding.logo} alt="Logo" className="h-11 w-11 rounded-2xl object-cover gold-glow" data-testid="login-brand-logo" />
            ) : (
              <div className="h-11 w-11 rounded-2xl bg-gold flex items-center justify-center gold-glow">
                <Airplane size={22} weight="fill" className="text-[hsl(var(--gold-foreground))] -rotate-45" />
              </div>
            )}
            <div>
              <div className="font-display text-xl leading-none" data-testid="login-brand-name">{branding.companyName}</div>
              <div className="text-xs text-white/70 mt-1 tracking-widest uppercase">Enterprise Travel ERP</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-5xl leading-[1.05] tracking-tight" data-testid="login-tagline">
              {branding.tagline}
            </h1>
            <p className="mt-4 text-white/75 max-w-md leading-relaxed">
              Umrah · International · Domestic — quotations, vendors, hotels, payments and analytics in one refined workspace.
            </p>
            <div className="mt-8 h-1 w-16 rounded-full gold-underline" />
          </div>
          <div className="flex items-center gap-6 text-white/60 text-xs">
            <div className="flex items-center gap-2"><Compass size={14} weight="bold" className="text-gold-hi" /> Trusted by pilgrims & travellers</div>
            <div className="hidden xl:block">© {new Date().getFullYear()} {branding.companyName}</div>
          </div>
        </div>
      </div>

      {/* Auth panel */}
      <div className="flex-1 flex items-center justify-center p-6 relative brand-gradient-soft">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            {branding.logo ? (
              <img src={branding.logo} alt="Logo" className="inline-block h-14 w-14 rounded-2xl object-cover mb-4 gold-glow" />
            ) : (
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient text-white mb-4 gold-glow">
                <Airplane size={26} weight="fill" className="-rotate-45" />
              </div>
            )}
            <h1 className="font-display text-3xl font-bold tracking-tight">{branding.companyName}</h1>
            <p className="text-muted-foreground mt-1 text-sm">Enterprise Travel ERP</p>
          </div>
          <Card className="travel-shadow-lg border-border/60">
            <CardContent className="p-8">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase text-gold font-semibold mb-2">
                  <span className="h-px w-6 bg-gold-deep inline-block" /> Welcome back
                </div>
                <h2 className="font-heading text-2xl font-bold">Sign in to your account</h2>
                <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2.5" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={loading} className="w-full btn-gold h-11 font-semibold text-sm tracking-wide" data-testid="login-submit">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="text-center mt-6 text-xs text-muted-foreground">
            © {new Date().getFullYear()} Aynoor Travels and Tours
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
